import React, { useEffect, useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import Textarea from "components/ui/Textarea";
import {
  CRM_ACTIVITY_TYPE_LABELS,
  CRM_STAGE_OPTIONS,
  getStageLabel,
} from "constants/crm";

const emptyForm = () => ({
  id: undefined,
  name: "",
  whatsapp: "",
  email: "",
  notes: "",
  interest: "",
  listing: "",
  stage: "novo",
  follow_up_at: "",
});

function toDatetimeLocal(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatTimelineAt(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const LeadDetailDrawer = ({
  open,
  mode = "edit",
  lead,
  stageOptions = CRM_STAGE_OPTIONS,
  saving,
  onClose,
  onSave,
  onDelete,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [noteText, setNoteText] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (mode === "create" || !lead) {
      setForm(emptyForm());
      setNoteText("");
      setError(null);
      return;
    }
    setForm({
      id: lead.id,
      name: lead.name || "",
      whatsapp: lead.whatsapp_display || lead.whatsapp_e164 || "",
      email: lead.email || "",
      notes: lead.notes || "",
      interest: lead.interest || "",
      listing: lead.listing || lead.interest || "",
      stage: lead.stage || "novo",
      follow_up_at: toDatetimeLocal(lead.follow_up_at),
    });
    setNoteText("");
    setError(null);
  }, [open, mode, lead]);

  if (!open) return null;

  const timeline = Array.isArray(lead?.timeline) ? [...lead.timeline].reverse() : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError("Nome é obrigatório.");
      return;
    }
    if (!form.whatsapp.trim()) {
      setError("WhatsApp é obrigatório.");
      return;
    }

    const payload = {
      id: form.id,
      name: form.name.trim(),
      whatsapp: form.whatsapp.trim(),
      email: form.email.trim(),
      notes: form.notes.trim(),
      interest: form.interest.trim() || form.listing.trim(),
      listing: form.listing.trim() || form.interest.trim(),
      stage: form.stage,
      follow_up_at: form.follow_up_at
        ? new Date(form.follow_up_at).toISOString()
        : null,
      source: form.id ? undefined : "web",
    };

    if (noteText.trim()) {
      payload.activity = { type: "note", text: noteText.trim() };
    }

    try {
      await onSave(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar lead.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-stretch sm:justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        aria-label="Fechar painel"
        onClick={onClose}
      />
      <aside className="relative z-10 flex h-[min(92vh,100%)] w-full max-w-md flex-col rounded-t-2xl border border-border bg-card shadow-elevation-xl sm:h-full sm:rounded-none sm:border-l sm:border-y-0 sm:border-r-0">
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-muted-foreground/30 sm:hidden" aria-hidden />
        <div className="flex items-center justify-between border-b border-border bg-muted/30 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              {mode === "create" ? "Novo lead" : "Editar lead"}
            </h2>
            {lead?.stage && mode === "edit" && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Estágio atual: {getStageLabel(lead.stage, stageOptions)}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
            aria-label="Fechar"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <Input
              label="Nome"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
            <Input
              label="WhatsApp"
              required
              value={form.whatsapp}
              onChange={(e) =>
                setForm((f) => ({ ...f, whatsapp: e.target.value }))
              }
              placeholder="(11) 99999-9999"
            />
            <Input
              label="E-mail"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
            <Input
              label="Empreendimento / interesse"
              value={form.listing}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  listing: e.target.value,
                  interest: e.target.value,
                }))
              }
            />
            <Select
              label="Estágio"
              options={stageOptions.map((s) => ({
                value: s.value,
                label: s.label,
              }))}
              value={form.stage}
              onChange={(v) => setForm((f) => ({ ...f, stage: v }))}
            />
            <Input
              label="Follow-up"
              type="datetime-local"
              value={form.follow_up_at}
              onChange={(e) =>
                setForm((f) => ({ ...f, follow_up_at: e.target.value }))
              }
            />
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Observações</label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={3}
              />
            </div>

            {mode === "edit" && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Adicionar nota na timeline
                </label>
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={2}
                  placeholder="Ex.: Cliente pediu retorno amanhã"
                />
              </div>
            )}

            {mode === "edit" && timeline.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Timeline</p>
                <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {timeline.map((entry) => (
                    <li
                      key={entry.id}
                      className="rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs"
                    >
                      <div className="flex items-center justify-between gap-2 text-muted-foreground">
                        <span className="font-medium text-foreground">
                          {CRM_ACTIVITY_TYPE_LABELS[entry.type] || entry.type}
                        </span>
                        <span>{formatTimelineAt(entry.at)}</span>
                      </div>
                      <p className="mt-1 text-foreground whitespace-pre-wrap">
                        {entry.text}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-border px-5 py-4">
            {mode === "edit" && lead?.whatsapp_url && (
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={() =>
                  window.open(lead.whatsapp_url, "_blank", "noopener,noreferrer")
                }
              >
                <Icon name="MessageCircle" size={16} />
                WhatsApp
              </Button>
            )}
            {mode === "edit" && onDelete && (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                disabled={saving}
                onClick={() => onDelete(lead)}
              >
                Excluir
              </Button>
            )}
            <div className="ml-auto flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Salvando…" : "Salvar"}
              </Button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
};

export default LeadDetailDrawer;
